import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { toast } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';

import { apiHeader, BaseURL } from '../../config/apiUrl';
import { Get, Patch, Post } from '../../Axios/AxiosFunctions';
import SideBarSkeleton from '../../Component/SideBarSkeleton';
import CopyToClipboardButton from '../../Component/ClickCopy';
import { Button } from '../../Component/Button/Button';
import PreviewListing from '../PreviewListing';
import Interests from '../Interests';
import EditListing from '../EditListing';
import NotesList from '../NotesList';
import ListingFolderDetail from '../TeamFolder/ListingFolderDetail';
import TaskManagerAllProjects from '../TaskManager/TaskManagerAllProjects';
import EditEntity from '../Entity/sections/EditEntity';
import TaxReturnConvertor from '../TaxReturnConvertor';
import ExtractionReview from '../ExtractionReview';
import ListingCommunications from '../ListingCommunications';
import SuggestedUpdates from '../SuggestedUpdates';
import BviViewer from '../BviViewer';
import FinancialTool from '../FinancialTool';
import AddUserToVipListModal from '../../modals/AddUserToVipListModal';
import classes from './ListingDetail.module.css';

const ListingDetail = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const superAdminEmail = process.env.REACT_APP_SUPER_ADMIN_EMAIL;
  const {
    user,
    access_token: accessToken,
    company_id: companyId,
  } = useSelector((state) => state.authReducer);
  const isAdmin = user?.role?.some((item) =>
    ['super-admin', 'admin', 'executive', 'hr', 'sales'].includes(item),
  );
  const allowed = [
    'mjn@denverbbs.com',
    'michael@denverbbs.com',
    superAdminEmail,
  ].includes(user?.email);
  // Pilot gate for the financial extraction tool. Kept separate from `allowed` above so
  // widening it later does not quietly hand out the other previews too. The API enforces
  // the same restriction; this only keeps the tab out of everyone else's way.
  const canExtract =
    user?.role?.some((r) => ['super-admin', 'admin'].includes(r)) ?? false;
  // Same roles the communications and suggested-updates APIs accept.
  const canSeeComms =
    user?.role?.some((r) => ['super-admin', 'admin', 'broker'].includes(r)) ??
    false;

  const currentDomain = window.location.origin;
  let domain = currentDomain?.includes('bbsadmins')
    ? currentDomain.replace('bbsadmins', 'denverbbs')
    : currentDomain.replace('admin', 'users');

  const [searchParams] = useSearchParams();
  const companyName = decodeURIComponent(searchParams.get('company-name'));
  const tab = searchParams.get('tab');
  const listingID = searchParams.get('id');
  const previewRef = useRef(null);

  // This list gates ?tab=<name> deep links: anything absent silently falls back to
  // 'listing'. 'financial' and 'extraction' both render tabs but were never added, so a
  // link straight to either one quietly landed on the listing page instead.
  const tabs = [
    'listing',
    'data-room',
    'notes',
    'interests',
    'projects',
    'entity',
    'bvi',
    'financial',
    'extraction',
    'communications',
    'suggested-updates',
  ];

  const [tabValue, setTabValue] = React.useState(
    tab && tabs.includes(tab) ? tab : 'listing',
  );
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [business, setBusiness] = useState(null);
  const [status, setSharedStatus] = useState(searchParams.get('status'));
  const [listingValue, setListingValue] = useState('view');
  const [folderRoot, setFolderRoot] = useState('');
  const [entityId, setEntityId] = useState(null);

  const [isAdding, setIsAdding] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [vipUsers, setVipUsers] = useState(null);
  const [isSendingSoldEmail, setIsSendingSoldEmail] = useState(false);
  const [pendingSuggestions, setPendingSuggestions] = useState(0);

  // The tab label carries the count so a broker sees there is something to
  // review without opening it.
  const loadPendingSuggestions = useCallback(async () => {
    if (!listingID || !canSeeComms) return;
    const res = await Get(
      BaseURL(`suggested-updates/business/${listingID}/count`),
      accessToken,
      companyId,
      false,
    );
    setPendingSuggestions(res?.data?.data?.pending || 0);
  }, [listingID, canSeeComms, accessToken, companyId]);

  useEffect(() => {
    loadPendingSuggestions();
  }, [loadPendingSuggestions]);

  const handleSendSoldCongratulations = async () => {
    if (!listingID) return;
    setIsSendingSoldEmail(true);
    try {
      const response = await Post(
        BaseURL('business/admin/send-sold-congratulations'),
        { business: listingID },
        apiHeader(accessToken),
        companyId,
      );
      if (response !== undefined) {
        const sent = response?.data?.sent ?? 0;
        toast.success(
          sent > 0
            ? `Congratulations email sent to ${sent} buyer(s).`
            : response?.data?.message || 'No buyers attached to this listing.',
        );
      }
    } catch (error) {
      console.error('send sold congratulations failed', error);
      toast.error('Failed to send congratulations email.');
    } finally {
      setIsSendingSoldEmail(false);
    }
  };

  const [permVip, setPermVip] = useState(isAdmin);

  const onTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const onListingChange = (event, newValue) => {
    setListingValue(newValue);
  };

  const getFolderRoot = async () => {
    const response = await Get(
      BaseURL(`data-room/business/${listingID}`),
      accessToken,
    );
    if (response != undefined) {
      let brokers = response?.data?.data?.business?.broker || [];
      brokers = brokers.map((broker) => broker.toString());
      if (brokers.includes(user?._id?.toString())) setPermVip(true);

      setFolderRoot(response?.data?.data?._id);
      setBusiness(response?.data?.data?.business);
      console.log('here', response?.data?.data);
      setEntityId(response?.data?.data?.entity || null);
    }
  };

  const getVipUsers = async () => {
    const url = BaseURL(`business/${listingID}/vip-users`);

    const response = await Get(url, accessToken);
    if (response !== undefined) {
      console.log('vipUsers:', response?.data?.vipUsers);
      setVipUsers(response?.data?.vipUsers?.buyer);
    }
  };

  const addUsersToVipList = async (e) => {
    setIsAdding(true);
    const url = BaseURL(`users/admin/add-to-vip`);
    const body = {
      businessId: listingID,
      ...e,
    };
    const response = await Patch(url, body, apiHeader(accessToken), companyId);
    if (response !== undefined) {
      setIsAdding(false);
      setVipUsers(response?.data?.data);
      toast.success('Vip users updated successfully');
      setIsOpenModal('');
    } else {
      setIsAdding(false);
    }
  };

  const handleAfterPrint = useCallback(() => {
    setIsPdfDownloading(false);
    if (previewRef.current) {
      const hiddenComponents =
        previewRef.current.querySelectorAll('.hidden-component');
      hiddenComponents.forEach(
        (component) => (component.style.display = 'block'),
      );
    }
  }, []);

  const handleBeforePrint = useCallback(() => {
    if (previewRef.current) {
      setIsPdfDownloading(true);
    }
    return Promise.resolve();
  }, []);

  const reactToPrintContent = () => {
    const hiddenComponents =
      previewRef.current.querySelectorAll('.hidden-component');
    hiddenComponents.forEach((component) => (component.style.display = 'none'));
    return previewRef.current;
  };

  const pintPage = useReactToPrint({
    documentTitle: `${companyName}`,
    onAfterPrint: handleAfterPrint,
    onBeforePrint: handleBeforePrint,
  });

  const generatePDF = async () => {
    setIsPdfGenerating(true);
    const url = BaseURL(`leads/generate-pdf`);
    const response = await Post(url, { slug }, apiHeader(accessToken), companyId);
    console.log('response', response);

    if (response?.status == 201 || response?.status == 200) {
      toast.success('PDF generated successfully');
    } 
    setIsPdfGenerating(false);
  }

  useEffect(() => {
    getFolderRoot();
    getVipUsers();
  }, []);

  const isPaidClient =
    localStorage.getItem('testtest') == 'true' ? true : false;

  return (
    <SideBarSkeleton>
      <div className={classes.mainContainer}>
        <div className={classes.listingHead}>
          <h4>{companyName || 'Listing Detail'}</h4>
          <Button
            label="Back"
            onClick={() => navigate(-1)}
          />
        </div>
        <Box sx={{ width: '100%', typography: 'body1' }}>
          <TabContext value={tabValue}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList
                onChange={onTabChange}
                aria-label="Listing Detail Tab"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Listing" value="listing" />
                {(status != 'prospect' || business?.owner?.length) && (
                  <Tab label="Deal Room" value="data-room" />
                )}
                {canSeeComms && (
                  <Tab label="communications" value="communications" />
                )}
                <Tab label="Notes" value="notes" />
                {status != 'prospect' && (
                  <Tab label="Interests" value="interests" />
                )}
                <Tab label="Projects" value="projects" />
                {entityId && <Tab label="Entity" value="entity" />}
                <Tab label="bvi" value="bvi" />
                <Tab label="financial tool" value="financial" />
                {canExtract && (
                  <Tab label="extraction review" value="extraction" />
                )}
                {canSeeComms && (
                  <Tab
                    label={
                      pendingSuggestions
                        ? `suggested updates (${pendingSuggestions})`
                        : 'suggested updates'
                    }
                    value="suggested-updates"
                  />
                )}
              </TabList>
            </Box>
            <TabPanel value="listing" style={{ padding: '0' }}>
              <div className={classes.toolBar}>
                <RadioGroup
                  row
                  name="row-radio-buttons-group"
                  defaultValue="view"
                  onChange={onListingChange}
                >
                  <FormControlLabel
                    value="view"
                    control={<Radio />}
                    label="View"
                  />
                  <FormControlLabel
                    value="edit"
                    control={<Radio />}
                    label="Edit"
                  />
                </RadioGroup>
                {status != 'prospect' && permVip && (
                  <Button
                    onClick={() => {
                      setIsOpenModal(true);
                    }}
                    label={'Add Buyers To VIP List'}
                    className={classes.btn}
                  />
                )}
                {listingValue == 'view' && (
                  <Button
                    onClick={() => {
                      pintPage(reactToPrintContent);
                    }}
                    label={isPdfDownloading ? 'Wait...' : 'Print Page'}
                    className={classes.btn}
                    disabled={isPdfDownloading}
                  />
                )}
                {listingValue == 'view' && (
                  <Button
                    onClick={generatePDF}
                    label={isPdfGenerating ? 'Wait...' : 'Generate PDF'}
                    className={classes.btn}
                    disabled={isPdfGenerating}
                  />
                )}
                {listingValue == 'view' &&
                  status === 'sold' &&
                  user?.email === 'developer@denverbbs.com' && (
                    <Button
                      onClick={handleSendSoldCongratulations}
                      label={
                        isSendingSoldEmail
                          ? 'Sending...'
                          : 'Send Congratulations Email'
                      }
                      className={classes.btn}
                      disabled={isSendingSoldEmail}
                    />
                  )}
                {[
                  'active',
                  'under-contract',
                  'under LOI',
                  'sold',
                  'seller-review',
                  'pocket',
                  'business-services',
                ].includes(status) &&
                  listingValue == 'view' && (
                    <CopyToClipboardButton
                      text={`${domain}/buy-a-business/${slug}`}
                    />
                  )}
              </div>
              <div>
                {listingValue == 'view' ? (
                  <div className={classes.editContainer}>
                    <PreviewListing previewRef={previewRef} />
                  </div>
                ) : (
                  <div className={classes.editContainer}>
                    <EditListing
                      flag={true}
                      setSharedStatus={setSharedStatus}
                    />
                  </div>
                )}
              </div>
            </TabPanel>
            <TabPanel value="data-room" style={{ padding: '0 0 0 15px' }}>
              <div className={classes.container}>
                <ListingFolderDetail
                  folderRoot={folderRoot}
                  business={listingID}
                />
              </div>
            </TabPanel>
            <TabPanel value="notes" style={{ padding: '0' }}>
              <div className={classes.container}>
                <NotesList slug={slug} status={status} />
              </div>
            </TabPanel>
            {status != 'prospect' && (
              <TabPanel value="interests" style={{ padding: '0' }}>
                <div className={classes.container}>
                  <Interests
                    isShowAll={true}
                    listingID={listingID}
                    companyName={companyName}
                  />
                </div>
              </TabPanel>
            )}
            <TabPanel value="projects" style={{ padding: '0' }}>
              <div className={classes.container}>
                <TaskManagerAllProjects
                  isShowAll={true}
                  listingID={listingID}
                  isShowAddTask
                />
              </div>
            </TabPanel>
            {entityId && (
              <TabPanel value="entity" style={{ padding: '0' }}>
                <div className={classes.container}>
                  <EditEntity _id={entityId} />
                </div>
              </TabPanel>
            )}

            <TabPanel value="bvi" style={{ padding: '0' }}>
              <div className={classes.container}>
                <BviViewer listingId={listingID} companyName={companyName} />
              </div>
            </TabPanel>

            <TabPanel value="financial" style={{ padding: '0' }}>
              <div className={classes.container}>
                <FinancialTool />
              </div>
            </TabPanel>

            <TabPanel value="extraction" style={{ padding: '0' }}>
              {/* a hand-typed ?tab=extraction must not reach the tool either */}
              {canExtract && (
                <div className={classes.container}>
                  <ExtractionReview listingId={listingID} />
                </div>
              )}
            </TabPanel>

            <TabPanel value="communications" style={{ padding: '0' }}>
              {canSeeComms && (
                <div className={classes.container}>
                  <ListingCommunications listingId={listingID} />
                </div>
              )}
            </TabPanel>

            <TabPanel value="suggested-updates" style={{ padding: '0' }}>
              {canSeeComms && (
                <div className={classes.container}>
                  <SuggestedUpdates
                    listingId={listingID}
                    onPendingChange={setPendingSuggestions}
                  />
                </div>
              )}
            </TabPanel>
          </TabContext>
        </Box>
      </div>
      {isOpenModal && (
        <AddUserToVipListModal
          show={isOpenModal}
          setShow={setIsOpenModal}
          onClick={addUsersToVipList}
          data={vipUsers}
          isLoading={isAdding}
        />
      )}
    </SideBarSkeleton>
  );
};

export default ListingDetail;
